import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import QuoteForm from '../../../components/feature/QuoteForm';

export default function BestContractors() {
  const qualities = [
    {
      title: 'Licensed & Insured',
      description: 'Verify contractor has proper licensing, liability insurance, and workers compensation.',
      icon: 'ri-shield-check-line',
    },
    {
      title: 'Local Experience',
      description: 'Choose contractors familiar with Lincoln climate, soil conditions, and regulations.',
      icon: 'ri-map-pin-line',
    },
    {
      title: 'Quality Materials',
      description: 'Best contractors use commercial-grade materials that meet industry standards.',
      icon: 'ri-star-line',
    },
    {
      title: 'Written Estimates',
      description: 'Detailed written quotes with scope, materials, timeline, and warranty information.',
      icon: 'ri-file-text-line',
    },
    {
      title: 'References',
      description: 'Established contractors provide references and examples of completed projects.',
      icon: 'ri-user-star-line',
    },
    {
      title: 'Warranty',
      description: 'Quality work backed by written warranties on materials and workmanship.',
      icon: 'ri-award-line',
    },
  ];

  const redFlags = [
    'No written contract or vague estimates',
    'Requires full payment upfront',
    'No physical business address or phone',
    'Pressure tactics or limited-time offers',
    'No insurance or licensing verification',
    'Significantly lower prices than competitors',
    'Cash-only payment requirements',
    'No references or portfolio of work',
  ];

  const faqs = [
    {
      question: 'How do I find the best asphalt contractor in Lincoln NE?',
      answer: 'Look for contractors with: proper licensing and insurance, 10+ years local experience, positive online reviews, references from recent projects, written warranties, and membership in industry associations. Get 3-4 estimates, verify credentials, and choose based on value (not just lowest price).',
    },
    {
      question: 'What questions should I ask paving contractors?',
      answer: 'Ask about: licensing and insurance, years in business, references, warranty terms, material specifications, project timeline, payment schedule, and what happens if problems arise. Request written estimates with detailed scope of work. Verify they handle permits and inspections.',
    },
    {
      question: 'How much should I pay upfront for paving work?',
      answer: 'Reputable contractors typically require 10-25% deposit to schedule work and order materials. Never pay full amount upfront. Standard payment schedule: 10-25% deposit, 50% at project midpoint, final 25-40% upon completion and your satisfaction. Avoid contractors demanding full payment before starting.',
    },
    {
      question: 'What warranty should paving contractors offer?',
      answer: 'Quality contractors offer 1-2 year workmanship warranties covering defects in installation. Material warranties vary: asphalt typically 1 year, concrete 1-2 years, sealcoating 1 year. Get warranties in writing with specific coverage terms. Reputable contractors stand behind their work.',
    },
    {
      question: 'Are online reviews reliable for choosing contractors?',
      answer: 'Online reviews provide valuable insights but verify across multiple platforms (Google, Facebook, BBB, Angie\'s List). Look for consistent patterns in reviews. A few negative reviews among many positive ones is normal. Beware of contractors with only perfect reviews or no online presence.',
    },
    {
      question: 'Why choose local Lincoln contractors over national chains?',
      answer: 'Local contractors understand Lincoln\'s climate, soil conditions, and regulations. They\'re invested in their reputation within the community. You can visit their office, see local projects, and get faster service. Local contractors are accountable and accessible for warranty work and future needs.',
    },
  ];

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Inter, sans-serif' }}>
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-primary-500 text-sm mb-4 tracking-wider">Resources / Contractor Guide</p>
            <h1 className="text-5xl md:text-6xl font-black mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Best Paving Contractors
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              How to choose top-rated asphalt and concrete contractors in Lincoln, Nebraska
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Why Choose Us */}
          <div className="mb-16 bg-gradient-to-br from-primary-600 to-primary-700 p-12 rounded-2xl text-white">
            <h2 className="text-4xl font-bold mb-6 text-center" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Why We're Lincoln's Top Choice
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-8">
              <div className="text-center">
                <div className="text-6xl font-black mb-2">20+</div>
                <p className="text-xl">Years Experience</p>
              </div>
              <div className="text-center">
                <div className="text-6xl font-black mb-2">5,000+</div>
                <p className="text-xl">Projects Completed</p>
              </div>
              <div className="text-center">
                <div className="text-6xl font-black mb-2">4.9★</div>
                <p className="text-xl">Average Rating</p>
              </div>
            </div>
          </div>

          {/* Qualities Grid */}
          <div className="mb-16">
            <h2 className="text-4xl font-bold mb-12 text-center text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              What Makes a Great Paving Contractor
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {qualities.map((quality, index) => (
                <div key={index} className="bg-gray-50 p-8 rounded-2xl border-2 border-gray-200 hover:border-primary-600 transition-all">
                  <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center mb-6">
                    <i className={`${quality.icon} text-3xl text-white`}></i>
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {quality.title}
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    {quality.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Detailed Content */}
          <div className="prose prose-lg max-w-none mb-16">
            <h2 className="text-4xl font-bold mb-6 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Choosing the Best Asphalt Contractor in Lincoln NE
            </h2>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Your driveway or parking lot is a significant investment that affects your property's value, safety, and appearance. Choosing the right contractor is critical to getting quality results that last. Poor workmanship leads to premature failure, costly repairs, and frustration.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Lincoln has many paving contractors, but quality varies dramatically. Some use inferior materials, skip proper base preparation, or lack experience with Nebraska's challenging climate. This guide helps you identify top contractors and avoid common pitfalls.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Verify Licensing and Insurance
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Always verify contractors have proper licensing and insurance. Nebraska requires contractors to be licensed for projects over $2,000. Request their license number and verify with the Nebraska Department of Labor. Unlicensed contractors may lack training and accountability.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Insurance is equally important. Contractors need general liability insurance (minimum $1 million) and workers compensation. Request certificates of insurance and verify they're current. Without insurance, you're liable for injuries or property damage during the project.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Reputable contractors readily provide licensing and insurance information. Those who hesitate or make excuses should be avoided. This verification protects you from liability and ensures you're working with legitimate professionals.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Local Experience Matters
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Choose contractors with extensive Lincoln experience. Local contractors understand our clay soils, freeze-thaw cycles, and drainage challenges. They know local building codes and have relationships with inspectors. This expertise prevents problems and ensures code compliance.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Look for contractors with 10+ years in Lincoln. Established businesses have proven track records and reputations to protect. They're invested in the community and accountable for their work. You can visit their office, see local projects, and verify their history.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Beware of out-of-town contractors or those without physical addresses. They may offer low prices but disappear when problems arise. Local contractors are accessible for warranty work, maintenance, and future projects. This long-term relationship provides peace of mind.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Get Multiple Written Estimates
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Obtain 3-4 written estimates from different contractors. Written estimates should include: detailed scope of work, material specifications (type, thickness, grade), labor costs, equipment costs, timeline, payment schedule, and warranty terms. Vague estimates lead to disputes and surprise costs.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Compare estimates carefully. The lowest price isn't always the best value. Low bids may use inferior materials, skip proper base work, or cut corners. The highest bid isn't necessarily better either. Look for detailed, transparent pricing that reflects quality work.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Ask contractors to explain their estimates. Quality contractors educate you about materials, processes, and why certain work is necessary. They answer questions patiently and provide references. This consultation reveals their expertise and professionalism.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Check References and Reviews
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Request references from recent projects similar to yours. Contact these references and ask about: quality of work, professionalism, timeline adherence, communication, problem resolution, and overall satisfaction. Visit completed projects if possible to see quality firsthand.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Check online reviews on Google, Facebook, Better Business Bureau, and Angie's List. Look for consistent patterns across platforms. A few negative reviews among many positive ones is normal. Beware of contractors with only perfect reviews (may be fake) or numerous complaints about similar issues.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Industry affiliations indicate professionalism. Look for membership in National Asphalt Pavement Association, American Concrete Institute, or local business associations. These organizations require standards compliance and provide ongoing education.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Understand Warranties
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Quality contractors offer written warranties covering workmanship and materials. Typical warranties: asphalt installation (1-2 years), concrete installation (1-2 years), sealcoating (1 year). Warranties should specify what's covered, exclusions, and claim procedures.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Understand warranty limitations. Normal wear, improper maintenance, and damage from external factors aren't covered. Warranties cover defects in materials or workmanship like premature cracking, settling, or surface deterioration. Get warranty terms in writing before signing contracts.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              A contractor's willingness to warranty their work indicates confidence in quality. Those who refuse warranties or offer only verbal promises should be avoided. Written warranties protect your investment and provide recourse if problems occur.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Red Flags to Avoid
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Certain warning signs indicate unreliable contractors. Avoid those who: demand full payment upfront (standard is 10-25% deposit), pressure you with limited-time offers, can't provide licensing or insurance, have no physical address or phone, offer prices significantly below competitors, require cash-only payment, or refuse written contracts.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Be wary of door-to-door contractors offering deals on leftover materials. These are often scams using inferior materials and poor workmanship. Legitimate contractors don't need to solicit door-to-door - they have steady work from referrals and reputation.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Trust your instincts. If something feels wrong or too good to be true, it probably is. Take time to research contractors, verify credentials, and make informed decisions. Rushing into contracts with unvetted contractors leads to problems and regret.
            </p>
          </div>

          {/* Red Flags */}
          <div className="mb-16 bg-red-50 p-12 rounded-2xl border-2 border-red-200">
            <h2 className="text-4xl font-bold mb-8 text-center text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Warning Signs to Avoid
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {redFlags.map((flag, index) => (
                <div key={index} className="flex items-start gap-3">
                  <i className="ri-close-circle-fill text-2xl text-red-600 mt-1"></i>
                  <p className="text-lg text-gray-700">{flag}</p>
                </div>
              ))}
            </div>
          </div>

          {/* FAQs */}
          <div className="mb-16">
            <h2 className="text-4xl font-bold mb-12 text-center text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Frequently Asked Questions
            </h2>
            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-gray-50 p-8 rounded-2xl border-2 border-gray-200">
                  <h3 className="text-2xl font-bold mb-4 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {faq.question}
                  </h3>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="bg-gray-50 p-12 rounded-2xl">
            <h2 className="text-4xl font-bold mb-6 text-center text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Work With Lincoln's Best
            </h2>
            <p className="text-xl text-gray-700 text-center mb-8">
              Licensed, insured, and trusted by Lincoln homeowners for over 20 years
            </p>
            <QuoteForm />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
